import React from 'react';
import { clsx } from 'clsx';
import { BuildingStorefrontIcon, PaintBrushIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

export type PricingPath = 'brand' | 'artist';

const PATHS: { value: PricingPath; eyebrow: string; title: string; body: string; Icon: typeof BuildingStorefrontIcon }[] = [
    {
        value: 'brand',
        eyebrow: 'Use Wallmates as a',
        title: 'Brand',
        body: 'Sell wallpaper in your own Shopify store at your prices. We print and ship every order.',
        Icon: BuildingStorefrontIcon
    },
    {
        value: 'artist',
        eyebrow: 'Use Wallmates as an',
        title: 'Artist',
        body: 'License your designs to other brands and get paid a royalty on every order they sell.',
        Icon: PaintBrushIcon
    }
];

// #region PathPicker
/** The two ways to use Wallmates, as two big cards. Everything below the picker follows the choice. */
export const PathPicker: React.FunctionComponent<{ value: PricingPath; onChange: (path: PricingPath) => void }> = ({ value, onChange }): React.ReactElement => (
    <div role="group" aria-label="How you use Wallmates" className="grid gap-3 sm:grid-cols-2">
        {PATHS.map(({ value: path, eyebrow, title, body, Icon }) => {
            const active = path === value;
            return (
                <button
                    key={path}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onChange(path)}
                    className={clsx(
                        'group relative flex items-start gap-4 rounded-2xl border-2 p-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised sm:p-5',
                        active
                            ? 'border-blue-600 bg-blue-600/[0.06] dark:border-blue-400 dark:bg-blue-500/10'
                            : 'border-surface-border bg-surface hover:border-surface-border-strong'
                    )}
                >
                    <span className={clsx(
                        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition',
                        active ? 'bg-blue-600 text-white' : 'bg-surface-muted text-zinc-500 group-hover:text-zinc-700 dark:text-zinc-400 dark:group-hover:text-zinc-200'
                    )}>
                        <Icon className="h-6 w-6" aria-hidden />
                    </span>
                    <span className="min-w-0">
                        <span className="block text-xs font-medium text-zinc-500 dark:text-zinc-400">{eyebrow}</span>
                        <span className={clsx('block font-display text-2xl font-semibold tracking-tight', active ? 'text-blue-700 dark:text-blue-300' : 'text-black dark:text-white')}>{title}</span>
                        <span className="mt-1 block text-sm text-zinc-600 dark:text-zinc-400">{body}</span>
                    </span>
                    {active ? <CheckCircleIcon className="absolute right-3 top-3 h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden /> : null}
                </button>
            );
        })}
    </div>
);
